import {
  Box,
  Flex,
  HStack,
  Text,
  Grid,
} from '@chakra-ui/react';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import getValueWithUnit from 'lib/getValueWithUnit';
import { currencyUnits } from 'lib/units';
import { Skeleton } from 'toolkit/chakra/skeleton';
import AddressFromTo from 'ui/shared/address/AddressFromTo';
import TxEntity from 'ui/shared/entities/tx/TxEntity';
import TxStatus from 'ui/shared/statusTag/TxStatus';
import TimeWithTooltip from 'ui/shared/time/TimeWithTooltip';
import TxFee from 'ui/shared/tx/TxFee';
import TxWatchListTags from 'ui/shared/tx/TxWatchListTags';
import TxAdditionalInfo from 'ui/txs/TxAdditionalInfo';
import TxType from 'ui/txs/TxType';

type Props = {
  tx: Transaction;
  isLoading?: boolean;
};

const LatestTxsItem = ({ tx, isLoading }: Props) => {
  const dataTo = tx.to ? tx.to : tx.created_contract;
  const columnNum = config.UI.views.tx.hiddenFields?.value && config.UI.views.tx.hiddenFields?.tx_fee ? 2 : 3;

  return (
    <Grid
      gridTemplateColumns={{
        lg: columnNum === 2 ? '3fr minmax(auto, 180px)' : '3fr minmax(auto, 180px) 170px',
        xl: columnNum === 2 ? '3fr minmax(auto, 250px)' : '3fr minmax(auto, 275px) 170px',
      }}
      gridGap={8}
      width="100%"
      minW="700px"
      borderBottom="1px solid"
      borderColor="border.divider"
      p={4}
      display={{ base: 'none', lg: 'grid' }}
    >
      <Flex overflow="hidden" w="100%">
        <TxAdditionalInfo tx={tx} isLoading={isLoading} my="3px" />
        <Box ml={3} w="calc(100% - 40px)">
          <HStack flexWrap="wrap" my="3px">
            <TxType types={tx.transaction_types} txType={tx.type} isLoading={isLoading} />
            <TxStatus status={tx.status} errorText={tx.status === 'error' ? tx.result : undefined} isLoading={isLoading} />
            <TxWatchListTags tx={tx} isLoading={isLoading} />
          </HStack>
          <Flex
            alignItems="center"
            mt="7px"
            mb="3px"
          >
            <TxEntity
              isLoading={isLoading}
              hash={tx.hash}
              fontWeight="700"
            />
            <TimeWithTooltip
              timestamp={tx.timestamp}
              enableIncrement
              timeFormat="relative"
              isLoading={isLoading}
              color="text.secondary"
              flexShrink={0}
              ml={2}
            />
          </Flex>
        </Box>
      </Flex>
      <AddressFromTo
        from={tx.deposited_to ?? tx.from}
        to={tx.deposited_to ? null : dataTo}
        isLoading={isLoading}
        mode="compact"
      />
      <Flex flexDir="column" rowGap={3}>
        {!config.UI.views.tx.hiddenFields?.value && (
          <Skeleton loading={isLoading}>
            <Text as="span" whiteSpace="pre">Value </Text>
            <Text as="span" color="text.secondary">{getValueWithUnit(tx.value).dp(5).toFormat()} {currencyUnits.ether}</Text>
          </Skeleton>
        )}
        {!config.UI.views.tx.hiddenFields?.tx_fee && (
          <Skeleton loading={isLoading} display="flex" whiteSpace="pre">
            <Text as="span">Fee </Text>
            <TxFee tx={tx} accuracy={5} color="text.secondary" />
          </Skeleton>
        )}
      </Flex>
    </Grid>
  );
};

export default React.memo(LatestTxsItem);
